import { Fragment, useMemo } from 'react';

import { getBreadcrumbsSchema } from '@zola-helpers/client/dist/es/schema';
import { BreadcrumbsV2 } from '@zola/zola-ui/src/components/BreadcrumbsV2';
import { BreadcrumbV2LinkItemType } from '@zola/zola-ui/src/components/BreadcrumbsV2/BreadcrumbsV2';
import Schemas from '@zola/zola-ui/src/components/Schemas/Schemas';
import {
  desktopV2Only,
  excludeDesktopV2,
} from '@zola/zola-ui/src/styles/emotion/mixins/responsiveV2';

import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { pickCanonicalFacets } from '~/pages/couples/explore/util/canonicalUrl';
import {
  getStateBreadcrumb,
  getCategoryLandingPageBreadcrumb,
  getLandingPageBreadcrumb,
  getLocationBreadcrumb,
  getCanonicalFacetsBreadcrumb,
} from '~/util/breadcrumbUtils';
import { isSearchLocation } from '~/util/searchUtils';

import { PageSection } from '../../../PageSection/PageSection';

import styles from './searchBreadcrumb.module.less';

export const useSearchBreadcrumbs = () => {
  const { vendorSearch, facetsByVendorType, isSRPLinkExperiment } = useSearchResultsPageContext();

  const linkItems: BreadcrumbV2LinkItemType[] = useMemo(() => {
    const { selectedLocation, selectedTaxonomy } = vendorSearch;
    const selectedTaxonomyKey = selectedTaxonomy?.key;
    const selectedFacets = vendorSearch.selectedFacets || [];

    const items = [];

    if (!isSRPLinkExperiment) {
      items.push(getLandingPageBreadcrumb());
    }
    if (selectedTaxonomyKey) {
      if (!isSRPLinkExperiment) {
        items.push(getCategoryLandingPageBreadcrumb(selectedTaxonomyKey));
      }

      if (isSearchLocation(selectedLocation)) {
        // When the selected location is a city, also add a breadcrumb for the state
        if (selectedLocation.type === 'CITY') {
          const stateCode = selectedLocation.storefrontSearchPayload?.stateProvince;
          const stateSlug = selectedLocation.stateSlug;

          if (stateCode && stateSlug) {
            items.push(getStateBreadcrumb(selectedTaxonomyKey, stateCode, stateSlug));
          }
        }

        items.push(
          getLocationBreadcrumb(selectedTaxonomyKey, selectedLocation, facetsByVendorType)
        );

        const canonicalFacets = pickCanonicalFacets(selectedTaxonomyKey, selectedFacets, true);
        if (canonicalFacets.length > 0) {
          items.push(
            getCanonicalFacetsBreadcrumb(
              selectedTaxonomyKey,
              selectedLocation,
              canonicalFacets,
              facetsByVendorType
            )
          );
        }
      }
    }

    return items;
  }, [vendorSearch, facetsByVendorType, isSRPLinkExperiment]);

  return linkItems;
};

export const SearchBreadcrumb = () => {
  const linkItems = useSearchBreadcrumbs();

  return (
    <Fragment>
      <Schemas schemas={[getBreadcrumbsSchema(linkItems)]} />
      <BreadcrumbsV2 className={styles.breadcrumbs} linkItems={linkItems} disableBackLink />
    </Fragment>
  );
};

export const SearchResultsPageBreadcrumbs = ({ location }: { location: 'top' | 'bottom' }) => {
  return (
    <PageSection
      noPadding={location === 'top'}
      css={location === 'top' ? desktopV2Only : excludeDesktopV2}
    >
      <SearchBreadcrumb />
    </PageSection>
  );
};
